import { useState, useEffect, useCallback } from 'react';
import { Gavel, Car, Users, Upload, LogOut, Plus, Pencil, Trash2, Image as ImageIcon, Download, FileSpreadsheet, X, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { INPUT_CLS, LABEL_CLS } from '@/components/brand/BrandKit';
import { logError } from '@/utils/logger';
import {
  adminLogin, adminLogout, adminMe, adminListAuctions, adminCreateAuction, adminUpdateAuction, adminDeleteAuction,
  adminListVehicles, adminCreateVehicle, adminUpdateVehicle, adminDeleteVehicle, adminUploadImages, adminRemoveImage,
  adminImportExcel, templateUrl, adminListOffers, adminUpdateOffer, apiError, imgSrc,
  SOURCE_LABELS, STATUS_LABELS, VAT_LABELS,
} from '@/api/auction';

const EMPTY_V = { lot_no: '', make: '', model: '', year: '', mileage_km: '', vin: '', plate: '', colour: '', transmission: '', fuel: '', source: 'ex-rental', vat_treatment: 'vat-inclusive', availability_status: 'in-yard', reserve_price_aed: '', auction_id: '', grade: '', accident_history: '', service_history: '', description: '', featured: false, published: true };
const EMPTY_A = { title: '', starts_at: '', ends_at: '', location: '', description: '', published: true };
const OFFER_STATUSES = ['submitted', 'shortlisted', 'accepted', 'rejected', 'withdrawn'];
const FIELD = `${INPUT_CLS} border w-full px-3`;

const Sel = ({ label, value, onChange, options, testid }) => (
  <div><label className={LABEL_CLS}>{label}</label>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={FIELD}>
      {Object.entries(options).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select></div>
);
const Fld = ({ label, value, onChange, type = 'text', testid, ph = '' }) => (
  <div><label className={LABEL_CLS}>{label}</label>
    <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={ph} className={FIELD} /></div>
);

// ─────────── Login ───────────
function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try { const me = await adminLogin({ email, password }); onLogin(me); }
    catch (e2) { setErr(apiError(e2, 'Invalid credentials')); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0B0C] px-4">
      <form onSubmit={submit} data-testid="auction-admin-login" className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center gap-2 text-[#EE5A01]"><Gavel className="w-5 h-5" /><span className="font-heading font-black text-lg text-[#121212]">Auction Admin</span></div>
        <p className="mt-1 font-body text-sm text-[#666666]">Eurogulf Used Cars — auction management portal.</p>
        <div className="mt-6 space-y-4">
          <Fld label="Email" value={email} onChange={setEmail} type="email" testid="aa-email" />
          <div><label className={LABEL_CLS}>Password</label>
            <input data-testid="aa-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={FIELD} /></div>
          {err && <p data-testid="aa-login-error" role="alert" className="bg-red-50 border border-red-200 text-red-600 text-sm p-3 rounded-lg">{err}</p>}
          <button type="submit" data-testid="aa-login-submit" disabled={busy} className="btn-primary w-full disabled:opacity-50">{busy ? 'Signing in…' : 'Sign in'}</button>
        </div>
      </form>
    </div>
  );
}

// ─────────── Vehicle form dialog ───────────
function VehicleDialog({ open, onOpenChange, vehicle, auctions, onSaved }) {
  const [form, setForm] = useState(EMPTY_V);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const editing = !!vehicle?.id;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) { setForm(vehicle ? { ...EMPTY_V, ...vehicle, reserve_price_aed: vehicle.reserve_price_aed ?? '', year: vehicle.year ?? '', mileage_km: vehicle.mileage_km ?? '' } : EMPTY_V); setErr(''); }
  }, [open, vehicle]);

  const save = async () => {
    setBusy(true); setErr('');
    const payload = { ...form, year: Number(form.year) || 2020, mileage_km: Number(form.mileage_km) || 0, reserve_price_aed: form.reserve_price_aed === '' ? null : Number(form.reserve_price_aed) };
    try {
      const saved = editing ? await adminUpdateVehicle(vehicle.id, payload) : await adminCreateVehicle(payload);
      setForm((p) => ({ ...p, id: saved.id, images: saved.images }));
      onSaved(saved, !editing);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  const uploadImages = async (files) => {
    if (!form.id) { setErr('Save the vehicle first, then add images.'); return; }
    setUploading(true); setErr('');
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('files', f));
    try { const r = await adminUploadImages(form.id, fd); setForm((p) => ({ ...p, images: r.images })); onSaved({ ...form, images: r.images }, false); }
    catch (e) { setErr(apiError(e)); } finally { setUploading(false); }
  };
  const removeImg = async (url) => {
    try { const r = await adminRemoveImage(form.id, url); setForm((p) => ({ ...p, images: r.images })); } catch (e) { setErr(apiError(e)); }
  };

  const auctionOpts = { '': '— None —', ...Object.fromEntries((auctions || []).map((a) => [a.id, a.title])) };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogTitle className="font-heading font-black text-xl text-[#121212]">{editing ? 'Edit vehicle' : 'Add vehicle'}</DialogTitle>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Fld label="Lot no (auto if blank)" value={form.lot_no} onChange={(v) => set('lot_no', v)} testid="av-lot" />
          <Fld label="Make *" value={form.make} onChange={(v) => set('make', v)} testid="av-make" />
          <Fld label="Model *" value={form.model} onChange={(v) => set('model', v)} testid="av-model" />
          <Fld label="Year *" value={form.year} onChange={(v) => set('year', v)} type="number" testid="av-year" />
          <Fld label="Mileage (km) *" value={form.mileage_km} onChange={(v) => set('mileage_km', v)} type="number" testid="av-mileage" />
          <Fld label="Colour" value={form.colour} onChange={(v) => set('colour', v)} testid="av-colour" />
          <Fld label="Transmission" value={form.transmission} onChange={(v) => set('transmission', v)} testid="av-transmission" />
          <Fld label="Fuel" value={form.fuel} onChange={(v) => set('fuel', v)} testid="av-fuel" />
          <Fld label="VIN" value={form.vin} onChange={(v) => set('vin', v)} testid="av-vin" />
          <Fld label="Plate" value={form.plate} onChange={(v) => set('plate', v)} testid="av-plate" />
          <Fld label="Grade" value={form.grade} onChange={(v) => set('grade', v)} testid="av-grade" />
          <Sel label="Source" value={form.source} onChange={(v) => set('source', v)} options={SOURCE_LABELS} testid="av-source" />
          <Sel label="VAT treatment" value={form.vat_treatment} onChange={(v) => set('vat_treatment', v)} options={VAT_LABELS} testid="av-vat" />
          <Sel label="Availability" value={form.availability_status} onChange={(v) => set('availability_status', v)} options={STATUS_LABELS} testid="av-status" />
          <Fld label="Reserve (AED, admin only)" value={form.reserve_price_aed} onChange={(v) => set('reserve_price_aed', v)} type="number" testid="av-reserve" />
          <Sel label="Auction" value={form.auction_id || ''} onChange={(v) => set('auction_id', v)} options={auctionOpts} testid="av-auction" />
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Fld label="Accident history" value={form.accident_history} onChange={(v) => set('accident_history', v)} testid="av-accident" />
          <Fld label="Service history" value={form.service_history} onChange={(v) => set('service_history', v)} testid="av-service" />
        </div>
        <div className="mt-4"><label className={LABEL_CLS}>Description</label>
          <textarea data-testid="av-description" value={form.description} onChange={(e) => set('description', e.target.value)} className={`${FIELD} !h-auto min-h-[70px] py-2`} /></div>
        <div className="mt-4 flex gap-6">
          <label className="flex items-center gap-2 font-body text-sm"><input data-testid="av-featured" type="checkbox" checked={form.featured} onChange={(e) => set('featured', e.target.checked)} /> Featured</label>
          <label className="flex items-center gap-2 font-body text-sm"><input data-testid="av-published" type="checkbox" checked={form.published} onChange={(e) => set('published', e.target.checked)} /> Published (public)</label>
        </div>

        {/* Images */}
        <div className="mt-6 border-t border-black/10 pt-5">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-sm text-[#121212]">Photos {form.id ? '' : '(save first to add)'}</h3>
            <label className={`btn-outline !py-2 cursor-pointer ${!form.id ? 'opacity-50 pointer-events-none' : ''}`}>
              <ImageIcon className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload images'}
              <input data-testid="av-image-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files.length && uploadImages(e.target.files)} />
            </label>
          </div>
          {(form.images || []).length > 0 && (
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2">
              {form.images.map((url) => (
                <div key={url} className="relative aspect-square rounded-lg overflow-hidden border border-black/10 group">
                  <img src={imgSrc(url)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImg(url)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {err && <p data-testid="av-error" role="alert" className="mt-4 bg-red-50 border border-red-200 text-red-600 text-sm p-3 rounded-lg">{err}</p>}
        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={() => onOpenChange(false)} className="btn-outline">Close</button>
          <button type="button" data-testid="av-save" onClick={save} disabled={busy} className="btn-primary disabled:opacity-50">{busy ? 'Saving…' : (editing ? 'Save changes' : 'Create vehicle')}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── Auction form dialog ───────────
function AuctionDialog({ open, onOpenChange, auction, onSaved }) {
  const [form, setForm] = useState(EMPTY_A);
  const [err, setErr] = useState('');
  const editing = !!auction?.id;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  useEffect(() => { if (open) { setForm(auction ? { ...EMPTY_A, ...auction } : EMPTY_A); setErr(''); } }, [open, auction]);
  const save = async () => {
    try {
      if (editing) await adminUpdateAuction(auction.id, form); else await adminCreateAuction(form);
      onSaved();
    } catch (e) { setErr(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogTitle className="font-heading font-black text-xl text-[#121212]">{editing ? 'Edit auction' : 'New auction'}</DialogTitle>
        <div className="mt-4 space-y-4">
          <Fld label="Title *" value={form.title} onChange={(v) => set('title', v)} testid="aa-auction-title" ph="Weekly Fleet Auction — June W3" />
          <div className="grid grid-cols-2 gap-4">
            <Fld label="Starts *" value={form.starts_at} onChange={(v) => set('starts_at', v)} type="datetime-local" testid="aa-auction-start" />
            <Fld label="Closes *" value={form.ends_at} onChange={(v) => set('ends_at', v)} type="datetime-local" testid="aa-auction-end" />
          </div>
          <Fld label="Location" value={form.location} onChange={(v) => set('location', v)} testid="aa-auction-loc" />
          <div><label className={LABEL_CLS}>Description</label><textarea value={form.description} onChange={(e) => set('description', e.target.value)} className={`${FIELD} !h-auto min-h-[60px] py-2`} /></div>
          <label className="flex items-center gap-2 font-body text-sm"><input type="checkbox" checked={form.published} onChange={(e) => set('published', e.target.checked)} /> Published</label>
          {err && <p role="alert" className="bg-red-50 border border-red-200 text-red-600 text-sm p-3 rounded-lg">{err}</p>}
          <button type="button" data-testid="aa-auction-save" onClick={save} className="btn-primary w-full">{editing ? 'Save' : 'Create auction'}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── Dashboard ───────────
function Dashboard({ admin, onLogout }) {
  const [tab, setTab] = useState('vehicles');
  const [vehicles, setVehicles] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [offers, setOffers] = useState([]);
  const [vDialog, setVDialog] = useState(false);
  const [editV, setEditV] = useState(null);
  const [aDialog, setADialog] = useState(false);
  const [editA, setEditA] = useState(null);
  const [importAuction, setImportAuction] = useState('');
  const [importMsg, setImportMsg] = useState('');
  const [importing, setImporting] = useState(false);

  const loadVehicles = useCallback(() => adminListVehicles({ limit: 300 }).then((d) => setVehicles(d.vehicles)).catch(() => {}), []);
  const loadAuctions = useCallback(() => adminListAuctions().then((d) => setAuctions(d.auctions)).catch(() => {}), []);
  const loadOffers = useCallback(() => adminListOffers().then((d) => setOffers(d.offers)).catch(() => {}), []);
  useEffect(() => { loadVehicles(); loadAuctions(); loadOffers(); }, [loadVehicles, loadAuctions, loadOffers]);

  const delVehicle = async (id) => { if (window.confirm('Delete this vehicle?')) { await adminDeleteVehicle(id); loadVehicles(); } };
  const delAuction = async (id) => { if (window.confirm('Delete this auction?')) { await adminDeleteAuction(id); loadAuctions(); } };
  const changeOffer = async (id, status) => { await adminUpdateOffer(id, status); loadOffers(); };

  const doImport = async (file) => {
    setImporting(true); setImportMsg('');
    const fd = new FormData(); fd.append('file', file);
    try { const r = await adminImportExcel(fd, importAuction || null); setImportMsg(`Imported: ${r.created} created, ${r.updated} updated${r.errors.length ? `, ${r.errors.length} rows skipped` : ''}.`); loadVehicles(); }
    catch (e) { setImportMsg(apiError(e)); } finally { setImporting(false); }
  };

  const tabs = [{ id: 'vehicles', label: 'Vehicles', icon: Car }, { id: 'auctions', label: 'Auctions', icon: Gavel }, { id: 'offers', label: 'Offers', icon: Users }, { id: 'import', label: 'Import', icon: Upload }];

  return (
    <div className="min-h-screen bg-[#0B0B0C]">
      <header className="bg-[#121212] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white"><Gavel className="w-5 h-5 text-[#EE5A01]" /><span className="font-heading font-black">Auction Admin</span></div>
          <div className="flex items-center gap-4">
            <span className="font-body text-sm text-white/60 hidden sm:inline">{admin.email}</span>
            <button type="button" data-testid="aa-logout" onClick={onLogout} className="btn-outline !border-white/30 !text-white hover:!bg-white hover:!text-black !py-2"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1">
          {tabs.map((t) => (
            <button key={t.id} type="button" data-testid={`aa-tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-3 font-heading font-bold text-sm border-b-2 transition-colors ${tab === t.id ? 'border-[#EE5A01] text-white' : 'border-transparent text-white/50 hover:text-white'}`}><t.icon className="w-4 h-4" />{t.label}</button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {tab === 'vehicles' && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-black text-xl text-white">Vehicles <span className="text-white/40 text-base">({vehicles.length})</span></h2>
              <button type="button" data-testid="aa-add-vehicle" onClick={() => { setEditV(null); setVDialog(true); }} className="btn-primary !py-2"><Plus className="w-4 h-4" /> Add vehicle</button>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F5F3] text-[#666] font-heading text-xs uppercase tracking-wide">
                  <tr><th className="text-left px-4 py-3">Lot</th><th className="text-left px-4 py-3">Vehicle</th><th className="text-left px-4 py-3 hidden sm:table-cell">Source</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3 hidden sm:table-cell">Reserve</th><th className="px-4 py-3"></th></tr>
                </thead>
                <tbody className="divide-y divide-black/5">
                  {vehicles.map((v) => (
                    <tr key={v.id} data-testid={`aa-vehicle-row-${v.id}`} className="hover:bg-[#FAFAFA]">
                      <td className="px-4 py-3 font-mono text-xs text-[#EE5A01]">{v.lot_no}{v.featured && <Star className="inline w-3 h-3 ml-1 fill-[#EE5A01] text-[#EE5A01]" />}</td>
                      <td className="px-4 py-3 font-heading font-bold text-[#121212]">{v.year} {v.make} {v.model}<span className="block font-body font-normal text-xs text-[#999]">{Number(v.mileage_km).toLocaleString()} km {v.published ? '' : '· hidden'}</span></td>
                      <td className="px-4 py-3 text-[#666] hidden sm:table-cell">{v.source_label}</td>
                      <td className="px-4 py-3"><span className="text-xs bg-[#F0F0EE] px-2 py-1 rounded">{v.status_label}</span></td>
                      <td className="px-4 py-3 text-right font-heading text-[#121212] hidden sm:table-cell">{v.reserve_price_aed ? `AED ${v.reserve_price_aed.toLocaleString()}` : '—'}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button type="button" data-testid={`aa-vehicle-edit-${v.id}`} onClick={() => { setEditV(v); setVDialog(true); }} className="text-[#666] hover:text-[#EE5A01] p-1.5"><Pencil className="w-4 h-4" /></button>
                        <button type="button" data-testid={`aa-vehicle-delete-${v.id}`} onClick={() => delVehicle(v.id)} className="text-[#666] hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                  {vehicles.length === 0 && <tr><td colSpan="6" className="px-4 py-12 text-center text-[#999] font-body">No vehicles yet. Add one or import from Excel.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'auctions' && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-black text-xl text-white">Auctions</h2>
              <button type="button" data-testid="aa-add-auction" onClick={() => { setEditA(null); setADialog(true); }} className="btn-primary !py-2"><Plus className="w-4 h-4" /> New auction</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {auctions.map((a) => (
                <div key={a.id} className="bg-white rounded-2xl p-5">
                  <div className="flex items-start justify-between">
                    <h3 className="font-heading font-bold text-[#121212]">{a.title}</h3>
                    <div className="flex gap-1"><button type="button" onClick={() => { setEditA(a); setADialog(true); }} className="text-[#666] hover:text-[#EE5A01] p-1"><Pencil className="w-4 h-4" /></button><button type="button" onClick={() => delAuction(a.id)} className="text-[#666] hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button></div>
                  </div>
                  <p className="mt-2 font-body text-xs text-[#666]">{new Date(a.starts_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })} → {new Date(a.ends_at).toLocaleString('en-GB', { timeStyle: 'short' })}</p>
                  <p className="mt-1 font-body text-xs text-[#999]">{a.location} · {a.lot_count} lots · {a.published ? 'Published' : 'Draft'}</p>
                </div>
              ))}
              {auctions.length === 0 && <p className="text-white/50 font-body col-span-full">No auctions yet.</p>}
            </div>
          </div>
        )}

        {tab === 'offers' && (
          <div>
            <h2 className="font-heading font-black text-xl text-white mb-5">Buyer offers <span className="text-white/40 text-base">({offers.length})</span></h2>
            <div className="bg-white rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F5F3] text-[#666] font-heading text-xs uppercase tracking-wide">
                  <tr><th className="text-left px-4 py-3">Vehicle</th><th className="text-left px-4 py-3">Buyer</th><th className="text-right px-4 py-3">Offer</th><th className="text-left px-4 py-3">Status</th></tr>
                </thead>
                <tbody className="divide-y divide-black/5">
                  {offers.map((o) => (
                    <tr key={o.id} className="hover:bg-[#FAFAFA]">
                      <td className="px-4 py-3"><span className="font-heading font-bold text-[#121212]">{o.vehicle_label}</span><span className="block font-mono text-xs text-[#999]">{o.lot_no}</span></td>
                      <td className="px-4 py-3"><span className="text-[#121212]">{o.buyer_company}</span><span className="block text-xs text-[#999]">{o.buyer_name} · {o.buyer_phone}</span></td>
                      <td className="px-4 py-3 text-right font-heading font-black text-[#EE5A01]">AED {o.amount_aed.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <select data-testid={`aa-offer-status-${o.id}`} value={o.status} onChange={(e) => changeOffer(o.id, e.target.value)} className="text-xs border border-black/15 rounded px-2 py-1.5">
                          {OFFER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                  {offers.length === 0 && <tr><td colSpan="4" className="px-4 py-12 text-center text-[#999] font-body">No offers yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'import' && (
          <div className="max-w-xl">
            <h2 className="font-heading font-black text-xl text-white mb-5">Bulk update via Excel</h2>
            <div className="bg-white rounded-2xl p-6 space-y-5">
              <a href={templateUrl} data-testid="aa-template-download" className="btn-outline inline-flex"><Download className="w-4 h-4" /> Download Excel template</a>
              <p className="font-body text-sm text-[#666]">Vehicles are matched by <b>lot_no</b> — existing lots are updated, new ones are created. Leave lot_no blank to auto-assign.</p>
              <div>
                <label className={LABEL_CLS}>Assign imported lots to auction (optional)</label>
                <select data-testid="aa-import-auction" value={importAuction} onChange={(e) => setImportAuction(e.target.value)} className={FIELD}>
                  <option value="">— None —</option>
                  {auctions.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                </select>
              </div>
              <label className={`btn-primary inline-flex cursor-pointer ${importing ? 'opacity-50 pointer-events-none' : ''}`}>
                <FileSpreadsheet className="w-4 h-4" /> {importing ? 'Importing…' : 'Upload .xlsx'}
                <input data-testid="aa-import-file" type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => e.target.files[0] && doImport(e.target.files[0])} />
              </label>
              {importMsg && <p data-testid="aa-import-msg" className="font-body text-sm text-[#121212] bg-[#F5F5F3] p-3 rounded-lg">{importMsg}</p>}
            </div>
          </div>
        )}
      </main>

      <VehicleDialog open={vDialog} onOpenChange={setVDialog} vehicle={editV} auctions={auctions} onSaved={(saved, isNew) => { if (isNew) setEditV(saved); loadVehicles(); }} />
      <AuctionDialog open={aDialog} onOpenChange={setADialog} auction={editA} onSaved={() => { setADialog(false); loadAuctions(); }} />
    </div>
  );
}

export default function AuctionAdminPage() {
  const [admin, setAdmin] = useState(undefined);
  useEffect(() => { document.title = 'Auction Admin | Eurogulf'; adminMe().then(setAdmin).catch(() => setAdmin(null)); }, []);
  const logout = async () => { try { await adminLogout(); } catch (err) { logError('Admin logout', err); } setAdmin(null); };
  if (admin === undefined) return <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center text-white/50 font-body">Loading…</div>;
  if (!admin) return <Login onLogin={setAdmin} />;
  return <Dashboard admin={admin} onLogout={logout} />;
}
